import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

import { useListBarLocationsQuery } from '../../../redux/api/locationApi';
import { useListStocksQuery } from '../../../redux/api/stockApi';
import {
  useCreateBarDeliveryMutation,
  useGetBarLocationReportQuery,
} from '../../../redux/api/barDeliveryApi';
import { useApiErrorToast } from '../../../hooks/useApiError';
import BarArticlePicker from './BarArticlePicker';

const euro = (value) =>
  `${Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

/**
 * One form serves all three bar delivery types.
 *
 *   OUT  outbound sale — articles and the per-line cap come from warehouse stock
 *   RET  goods return  — articles and the cap come from what the bar still holds
 *   EMP  empties       — *Pfand-Rücknahmearten*, not articles. A pallet of
 *                        empties is a stack of interchangeable containers, so
 *                        the operator books the Gebinde and the shape it came
 *                        back in. Each group names its own kinds, because
 *                        "Leerkasten" means nothing next to a box of cups whose
 *                        reduced kind is a single cup.
 *
 * Returns and empties are bar-driven: the picker is only populated once a bar is
 * chosen, because the options depend on that bar's delivery history.
 *
 * OUT and RET are hard-capped — over them the server refuses and so does the
 * submit button. Empties are not: crates travel between bars on mixed pallets,
 * so the pool balance is shown and exceeding it only warns.
 */
const TYPE_CONFIG = {
  OUT: {
    heading: 'Bar-Lieferung anlegen',
    submitLabel: 'Lieferung anlegen',
    successLabel: 'Lieferung wurde angelegt',
    quantityLabel: 'Menge',
    capLabel: 'verfügbar',
    positionLabel: 'Artikel',
    fromStock: true,
    byDepositGroup: false,
    enforceCap: true,
  },
  RET: {
    heading: 'Bar-Retoure anlegen (Ware)',
    submitLabel: 'Retoure anlegen',
    successLabel: 'Retoure wurde angelegt',
    quantityLabel: 'Menge zurück',
    capLabel: 'bei der Bar',
    positionLabel: 'Artikel',
    fromStock: false,
    byDepositGroup: false,
    enforceCap: true,
  },
  EMP: {
    heading: 'Leergut-Rücknahme anlegen',
    submitLabel: 'Leergut-Rücknahme anlegen',
    successLabel: 'Leergut-Rücknahme wurde angelegt',
    quantityLabel: 'Menge Leergut',
    capLabel: 'offen',
    positionLabel: 'Pfandgruppe',
    pickerTitle: 'Pfandgruppe hinzufügen',
    searchPlaceholder: 'Pfandgruppe suchen...',
    emptyLabel:
      'Für diese Bar ist kein Pfand offen — es wurde noch keine pfandpflichtige Ware geliefert.',
    noMatchLabel: 'Keine weitere Pfandgruppe gefunden.',
    balanceTitle: 'noch offenes Leergut',
    fromStock: false,
    byDepositGroup: true,
    enforceCap: false,
  },
};

const BarDeliveryForm = ({ deliveryType = 'OUT' }) => {
  const config = TYPE_CONFIG[deliveryType];
  const eventId = useSelector((state) => state.auth.currentEvent?.id);

  const { data: barLocations = [] } = useListBarLocationsQuery(eventId, {
    skip: !eventId,
  });
  const { data: stocks = [] } = useListStocksQuery(
    { eventId },
    { skip: !eventId || !config.fromStock }
  );

  const [destinationId, setDestinationId] = useState('');
  const [lines, setLines] = useState([]);
  const [comment, setComment] = useState('');
  const [noteComment, setNoteComment] = useState('');

  const { data: barReport, isFetching: reportFetching } =
    useGetBarLocationReportQuery(destinationId, {
      skip: config.fromStock || !destinationId,
    });

  const [createDelivery, { isLoading }] = useCreateBarDeliveryMutation();
  const navigate = useNavigate();
  const apiErrorToast = useApiErrorToast();

  // Reset everything when the event or the delivery type changes — the option
  // lists and the caps are only valid within one event.
  useEffect(() => {
    setDestinationId('');
    setLines([]);
    setComment('');
    setNoteComment('');
  }, [eventId, deliveryType]);

  // Changing the bar invalidates the picked lines for the bar-driven flows,
  // because the caps came from the previously selected bar.
  useEffect(() => {
    if (!config.fromStock) setLines([]);
  }, [destinationId, config.fromStock]);

  const options = useMemo(() => {
    if (config.fromStock) {
      return stocks
        .filter((s) => s.sell_bar)
        .map((s) => ({
          key: s.article,
          label: s.article_name,
          sublabel: s.unit_name,
          available: s.quantity_available,
          articleId: s.article,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (config.byDepositGroup) {
      // Only Pfandgruppen appear here, never articles — which is also what
      // keeps a deposit-free article from being booked as Leergut at all. Each
      // way the Gebinde can come back is its own row, named by the group: a
      // bare crate for one, a single cup for another.
      return (barReport?.deposit_groups || []).flatMap((group) =>
        group.return_kinds.map((kind) => ({
          key: kind.deposit_kind_id,
          label: `${group.deposit_group_name} — ${kind.name}`,
          sublabel: kind.is_whole_gebinde
            ? `${euro(kind.deposit_per_unit)} / Gebinde`
            : `${euro(kind.deposit_per_unit)} / Stück · ${kind.units_per_return} = 1 Gebinde`,
          // The balance is the pool's, shown on every kind of that pool: a
          // single cup and a whole box draw down the same outstanding count.
          available: Number(group.outstanding),
          depositKindId: kind.deposit_kind_id,
          depositGroupId: group.deposit_group_id,
          unitsPerReturn: kind.units_per_return,
        }))
      );
    }

    // Goods return: what the bar still holds, already net of earlier returns.
    return (barReport?.items || [])
      .map((row) => ({
        key: row.article_id,
        label: row.article_name,
        sublabel: '',
        available: row.delivered_quantity,
        articleId: row.article_id,
      }))
      .filter((row) => row.available > 0)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [config.fromStock, config.byDepositGroup, stocks, barReport]);

  const capByKey = useMemo(
    () => Object.fromEntries(options.map((o) => [o.key, o.available])),
    [options]
  );

  // Every kind of one Pfandgruppe draws down the same pool, so they have to be
  // measured against it together — and in Gebinde, not in items: 350 returned
  // cups release one box, not 350.
  const bookedGebindeByGroup = useMemo(() => {
    const totals = {};
    lines.forEach((line) => {
      if (!line.depositGroupId) return;
      const perGebinde = line.unitsPerReturn || 1;
      totals[line.depositGroupId] =
        (totals[line.depositGroupId] || 0) + (line.quantity || 0) / perGebinde;
    });
    return totals;
  }, [lines]);

  const lineExceedsPool = (line) => {
    const cap = capByKey[line.key] ?? 0;
    return config.byDepositGroup
      ? (bookedGebindeByGroup[line.depositGroupId] || 0) > cap
      : (line.quantity || 0) > cap;
  };

  const addLine = (option) =>
    setLines((prev) =>
      prev.some((l) => l.key === option.key)
        ? prev
        : [
            ...prev,
            {
              key: option.key,
              label: option.label,
              sublabel: option.sublabel,
              articleId: option.articleId,
              depositKindId: option.depositKindId,
              depositGroupId: option.depositGroupId,
              unitsPerReturn: option.unitsPerReturn,
              quantity: 1,
            },
          ]
    );

  const removeLine = (key) =>
    setLines((prev) => prev.filter((l) => l.key !== key));

  const updateQuantity = (key, quantity) =>
    setLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, quantity } : l))
    );

  const overCap = lines.some(lineExceedsPool);
  const totalUnits = lines.reduce((sum, l) => sum + (l.quantity || 0), 0);
  // Over-returning empties is a real situation to record, so it must not block
  // the booking — only the two stock-backed flows are hard-capped here.
  const canSubmit =
    !!destinationId &&
    lines.length > 0 &&
    totalUnits > 0 &&
    !(config.enforceCap && overCap);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    const payload = {
      event_id: eventId,
      destination_id: destinationId,
      delivery_type: deliveryType,
      items: lines
        .filter((l) => l.quantity > 0)
        .map((l) =>
          config.byDepositGroup
            ? {
                deposit_kind_id: l.depositKindId,
                planned_quantity: l.quantity,
              }
            : { article_id: l.articleId, planned_quantity: l.quantity }
        ),
      ...(comment.trim() ? { comment: comment.trim() } : {}),
      ...(noteComment.trim()
        ? { delivery_note_comment: noteComment.trim() }
        : {}),
    };

    try {
      const result = await createDelivery(payload).unwrap();
      toast.success(config.successLabel);
      navigate(`/bar-delivery/${result.id}`);
    } catch (err) {
      apiErrorToast(err, 'Fehler beim Anlegen!');
    }
  };

  if (!eventId) {
    return (
      <div className='alert alert-warning'>Bitte zuerst ein Event wählen.</div>
    );
  }

  return (
    <div>
      <h4>{config.heading}</h4>

      {config.byDepositGroup && (
        <div className='alert alert-info mt-3'>
          Leergut wird je <b>Pfandgruppe</b> erfasst, nicht je Artikel — eine
          Palette Leergut ist ein Stapel gleicher Gebinde. Jede{' '}
          <b>Rücknahmeart</b> ist eine eigene Position mit eigenem Satz, damit
          auf der Abrechnung steht, was tatsächlich zurückkam.
        </div>
      )}

      <div className='row mt-3'>
        <div className='col-12 col-lg-8'>
          <div className='mb-3'>
            <label className='form-label'>Bar</label>
            <select
              className='form-select'
              value={destinationId}
              onChange={(e) => setDestinationId(e.target.value)}
            >
              <option value=''>Bar auswählen</option>
              {barLocations.map((bar) => (
                <option key={bar.id} value={bar.id}>
                  {bar.name}
                </option>
              ))}
            </select>
            {barLocations.length === 0 && (
              <div className='form-text text-danger'>
                Für dieses Event sind keine Bars angelegt.
              </div>
            )}
          </div>

          {!config.fromStock && !destinationId ? (
            <p className='text-muted'>
              {config.byDepositGroup
                ? 'Bitte zuerst eine Bar wählen — die Pfandgruppen ergeben sich aus den bestätigten Lieferungen an diese Bar.'
                : 'Bitte zuerst eine Bar wählen — die rücknehmbaren Artikel ergeben sich aus den bestätigten Lieferungen an diese Bar.'}
            </p>
          ) : reportFetching ? (
            <p className='text-muted'>Lade rücknehmbare Positionen...</p>
          ) : (
            <BarArticlePicker
              options={options}
              addedKeys={lines.map((l) => l.key)}
              onAdd={addLine}
              showStock
              strictCap={config.enforceCap}
              title={config.pickerTitle}
              searchPlaceholder={config.searchPlaceholder}
              emptyLabel={config.emptyLabel}
              noMatchLabel={config.noMatchLabel}
              balanceTitle={config.balanceTitle}
            />
          )}

          {lines.length > 0 && (
            <table className='table table-striped mt-4 align-middle'>
              <thead>
                <tr>
                  <th>{config.positionLabel}</th>
                  <th style={{ width: '160px' }}>{config.quantityLabel}</th>
                  <th style={{ width: '120px' }}>{config.capLabel}</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  const cap = capByKey[line.key] ?? 0;
                  const isOver = lineExceedsPool(line);
                  return (
                    <tr key={line.key}>
                      <td>
                        {line.label}
                        {line.sublabel && (
                          <span className='text-muted ms-2'>
                            ({line.sublabel})
                          </span>
                        )}
                      </td>
                      <td>
                        <input
                          type='number'
                          min='1'
                          className={`form-control${
                            isOver && config.enforceCap ? ' is-invalid' : ''
                          }`}
                          value={line.quantity}
                          onChange={(e) => {
                            const raw = e.target.value;
                            updateQuantity(
                              line.key,
                              raw === ''
                                ? ''
                                : Math.max(0, parseInt(raw, 10) || 0)
                            );
                          }}
                        />
                      </td>
                      <td
                        className={
                          isOver
                            ? config.enforceCap
                              ? 'text-danger'
                              : 'text-warning-emphasis'
                            : 'text-muted'
                        }
                      >
                        {cap}
                      </td>
                      <td>
                        <button
                          className='btn btn-outline-danger btn-sm'
                          onClick={() => removeLine(line.key)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className='col-12 col-lg-4 mt-4 mt-lg-0'>
          <div className='card'>
            <div className='card-body'>
              <h6 className='card-title'>Zusammenfassung</h6>
              <p className='mb-1'>
                <b>{lines.length}</b>{' '}
                {lines.length === 1 ? 'Position' : 'Positionen'}
              </p>
              <p>
                <b>{totalUnits}</b>{' '}
                {totalUnits === 1 ? 'Packstück' : 'Packstücke'}
              </p>

              <div className='mb-3'>
                <label className='form-label'>
                  Kommentar{' '}
                  <span className='text-muted'>
                    (optional, nur im System sichtbar)
                  </span>
                </label>
                <textarea
                  className='form-control'
                  rows='2'
                  maxLength='255'
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>

              <div className='mb-3'>
                <label className='form-label'>
                  Lieferschein-Hinweis{' '}
                  <span className='text-muted'>(wird gedruckt)</span>
                </label>
                <textarea
                  className='form-control'
                  rows='2'
                  maxLength='255'
                  value={noteComment}
                  onChange={(e) => setNoteComment(e.target.value)}
                />
              </div>

              {overCap &&
                (config.enforceCap ? (
                  <p className='text-danger'>
                    <i className='fas fa-exclamation-triangle me-1' />
                    Mindestens eine Position überschreitet die zulässige Menge.
                  </p>
                ) : (
                  <div className='alert alert-warning py-2'>
                    <i className='fas fa-exclamation-triangle me-1' />
                    Es wird mehr Leergut zurückgenommen als für diese Bar offen
                    ist. Das ist bei gemischten Paletten normal — die Rücknahme
                    wird gebucht und in der Pfandübersicht als Überhang
                    ausgewiesen.
                  </div>
                ))}

              <button
                className='btn btn-success w-100'
                disabled={!canSubmit || isLoading}
                onClick={handleSubmit}
              >
                {config.submitLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarDeliveryForm;
